// Simple feature-flag mechanism

import { useLocalStorage } from "@nens/react/hooks";

// - Environments: "production" | "staging" | "test" | "localhost"
export type Environment = "production" | "staging" | "test" | "localhost";

// Feature flags are intentionally defined here so the test-only controls and consumers share one registry.
export type FeatureKey = "workflows" | "model-schematisation-for-collaborators";

// Default per-environment feature configuration (edit here to change defaults).
const FEATURE_FLAGS: Record<FeatureKey, Record<Environment, boolean>> = {
  workflows: {
    production: false,
    staging: true,
    test: true,
    localhost: true,
  },
  "model-schematisation-for-collaborators": {
    production: false,
    staging: true,
    test: true,
    localhost: true,
  },
};

export const featureKeys = Object.keys(FEATURE_FLAGS) as FeatureKey[];

// Feature flags can be overridden in localStorage.
// If this feature is overridden there, return its stored boolean value from there.
// If it's not, or the stored value is not a boolean, return undefined so that the default can be used.
const getFeatureFlagOverride = (feature: FeatureKey): boolean | undefined => {
  if (typeof window === "undefined") return undefined;

  try {
    const storedFeatureFlags = JSON.parse(
      window.localStorage.getItem("feature-flags") ?? "{}"
    ) as Partial<Record<FeatureKey, boolean>>;
    const storedOverride = storedFeatureFlags[feature];
    return typeof storedOverride === "boolean" ? storedOverride : undefined;
  } catch {
    return undefined;
  }
};

const CURRENT_ENV: Environment = (() => {
  // Determine environment from the current URL when running in a browser.
  // Rules: "localhost" -> localhost, URL containing "test" -> test,
  // containing "staging" -> staging, otherwise production.
  if (
    typeof window !== "undefined" &&
    window.location &&
    typeof window.location.href === "string"
  ) {
    const url = window.location.href.toLowerCase();
    if (url.includes("localhost")) return "localhost";
    if (url.includes("test")) return "test";
    if (url.includes("staging")) return "staging";
    return "production";
  }

  // Non-browser fallback (e.g. server-side): assume production
  return "production";
})();

// Public API

export function isFeatureEnabled(feature: FeatureKey, env?: Environment): boolean {
  // We sometimes want to get the feature enabled flag for a different environment than the
  // current one, for instance to show what the value is in production in the feature
  // flags banner on test. In that case, 'env' is passed. If it isn't, use CURRENT_ENV.

  // If env is not passed, this feature flag may be overriden. Get the override value.
  // If env is passed, we are looking for the value in another environment, and override values
  // are for the current environment. So don't use overrides in the case.
  if (!env) {
    const override = getFeatureFlagOverride(feature);
    if (override !== undefined) {
      return override;
    }
  }

  // Otherwise, get the value from FEATURE_FLAGS.
  const targetEnv = env ?? CURRENT_ENV;
  const flagsForFeature = FEATURE_FLAGS[feature];
  return !!(flagsForFeature && flagsForFeature[targetEnv]);
}

export function useSetFeatureFlags(
  feature: FeatureKey
): [boolean | null, (enabled: boolean) => void] {
  const [featureFlags, setFeatureFlags] = useLocalStorage<Partial<Record<string, boolean>>>(
    "feature-flags",
    {}
  );

  const setSingleFeatureFlag = (enabled: boolean) => {
    setFeatureFlags({ ...featureFlags, [feature]: enabled });
  };
  const featureFlagState = featureFlags[feature] ?? null;

  return [featureFlagState, setSingleFeatureFlag];
}
